import React, { useEffect, useMemo, useRef, useState } from "react";
import type {
  Config,
  Layout,
  PlotData,
  PlotlyHTMLElement,
  PlotMouseEvent,
  PlotSelectionEvent,
  ViolinData,
} from "plotly.js";
import seedrandom from "seedrandom";
import { MAX_POINTS_TO_ANNOTATE } from "../../../../../constants/plotConstants";
import { usePlotlyLoader } from "../../../../../contexts/PlotlyLoaderContext";
import {
  AnnotationTail,
  applyAnnotationTailOverrides,
  calcAnnotationArrowWidth,
  calcAnnotationPositions,
  calcChromeAxisOverrides,
  calcExtraLinesMargin,
  calcViolinOutlineWidth,
  captureAnnotationTail,
  cloneFigureForExport,
  DataExplorerColorPalette,
  DEFAULT_CHROME_LINE_WIDTH,
  DEFAULT_VIOLIN_LINE_WIDTH,
  DEFAULT_PALETTE,
  getRange,
  hexToRgba,
  isEveryValueNull,
  legendLayoutFor,
  wantsLegendTraces,
  LEGEND_ALL,
  LegendKey,
  orderColorKeysByCount,
  NEUTRAL_FACET_FILL,
  onWebglContextRestored,
  orderContinuousPointsByBin,
  exportMarginFloor,
  releaseWebglContexts,
  resolveAnnotationTail,
  truncateFacetLabel,
} from "./plotUtils";
import usePlotResizer from "./usePlotResizer";
import installFacetSelectionDragLayer, {
  FacetSelectionConfig,
} from "./facetSelectionDragLayer";
import type ExtendedPlotType from "../../../ExtendedPlotType";

type Data = Record<string, any>;

interface Props {
  data: Data;
  xKey: string;
  hoverTextKey?: string;
  annotationTextKey?: string;
  height: number | "auto";
  colorMap: Map<LegendKey, string>;
  // colorData: per-point color key. Drives bgcolor on scatter traces and
  // (in the converged case where modes match) violin fillcolor.
  colorData?: any;
  // facetData: per-point facet key. Drives violin-track assignment. When
  // not supplied, falls back to colorData for backward compatibility with
  // existing callers and the converged case.
  facetData?: any;
  // groupKeys: track display order. When not supplied, derived from
  // [...colorMap.keys()] (which is the legend order under the old
  // single-array convention).
  groupKeys?: LegendKey[];
  continuousColorKey?: string;
  legendDisplayNames: Partial<Record<LegendKey, string>>;
  // facetDisplayNames: labels for violin-track names, keyed by the SAME
  // LEGEND_RANGE_* symbols legendDisplayNames uses — but facet_by and
  // color_by can each independently bin a continuous property, and when
  // they do, the shared Range symbols mean different bin boundaries on
  // each axis. legendDisplayNames must never be reused for track names in
  // that case (it would show color's bin ranges on facet's tracks). Falls
  // back to legendDisplayNames when not supplied, for callers whose facet
  // keys are non-continuous (or absent) and can't collide this way.
  facetDisplayNames?: Partial<Record<LegendKey, string>>;
  legendTitle?: string | null;
  // Reveals Plotly's own legend, built from the color keys. Data Explorer
  // hides it because it renders its own richer legend panel alongside the
  // plot (that's what the dummy traces below normally exist only to fake, for
  // downloaded images) — but embedded plots live outside Data Explorer with
  // no panel to fall back on, so for them Plotly's legend is all there is.
  // Mirrors PrototypeScatterPlot / SmallMultiplesScatter's identically named
  // prop.
  showBuiltinLegend?: boolean;
  // Whether facet_by and color_by resolve to the SAME underlying source
  // (see resolveColorMode / useDensity1DPlotData's own colorMatchesFacet
  // comment for the full rationale, including why this is NOT simply
  // "colorMode.target === 'facet'" — facet_by: "expansion" and
  // color_by: "expansion", explicit on both sides with neither deferring,
  // still read the identical source). `hiddenLegendValues` is populated
  // purely from clicks on the COLOR legend. When this is true, the legend
  // IS facet's own partition (or reads the identical data), so hiding a
  // legend entry legitimately hides that same violin track, and a track
  // may borrow its real color from colorMap. When false, facet's tracks
  // are a completely separate partition that merely happens to share
  // LEGEND_BOTH/LEGEND_OTHER's Symbol identity with color's —
  // hiddenLegendValues/colorMap must NOT be applied to them, or hiding
  // "Other" in the color legend would incorrectly also remove facet's
  // unrelated "Other" track. Defaults to `false` (the safe, facet_by-
  // agnostic behavior) for callers that don't pass it.
  colorMatchesFacet?: boolean;
  // Facets toggled off via the "Facets" panel (shown when color_by/facet_by
  // diverge — colorMatchesFacet false). Independent of hiddenLegendValues;
  // see this prop's own docs on the `facetHiddenValues` local below for how
  // the two combine.
  hiddenFacetValues?: Set<LegendKey>;
  selectedPoints?: Set<number>;
  onClickPoint?: (pointIndex: number, ctrlKey: boolean) => void;
  onMultiselect?: (pointIndices: number[]) => void;
  onClickResetSelection?: () => void;
  pointVisibility?: boolean[];
  useSemiOpaqueViolins?: boolean;
  onLoad?: (plot: ExtendedPlotType) => void;
  hiddenLegendValues?: any;
  // optional styling
  pointSize?: number;
  pointOpacity?: number;
  outlineWidth?: number;
  palette?: DataExplorerColorPalette;
  // Point labels. Their own setting rather than a ratio of the axis
  // font: these sit inside the plot area competing with the data.
  annotationFontSize?: number;
  xAxisFontSize?: number;
  yAxisFontSize?: number;
  // When true, facet tracks whose points are entirely null are kept as
  // labeled "(no data)" placeholders instead of being dropped. Used by
  // expanded plots so a paginated transcript window renders at full size even
  // when the dataset doesn't measure every transcript in the window.
  placeholderEmptyTracks?: boolean;
  // When true, box/lasso selection is replaced with a custom drag whose
  // marquee is confined to the violin track (facet) it starts in, and cannot
  // escape into a neighbouring track. Shift adds to the selection within that
  // same locked track. See facetSelectionDragLayer.
  enforceSingleFacetSelection?: boolean;
  // The points to put text labels on. Diverges from `selectedPoints` only under
  // facet_by === "expansion": there, `selectedPoints` re-expands a model to
  // every region it appears in, while this stays the handful of contacted
  // points (see useSelection's annotation channel). Falls back to
  // `selectedPoints` when omitted, so the labeled set is unchanged elsewhere.
  pointsToAnnotate?: Set<number>;
  // The count shown in the "N points selected" fallback when there are too many
  // to label. The wrapper threads selection.size (model count under collapse);
  // defaults to the selected-point count when omitted.
  selectionCount?: number;
  // Transient view state to start from, instead of this instance building
  // its own up from scratch — see PrototypeScatterPlot's identical props.
  // Newly added so this renderer can be used as the export modal's hidden
  // preview instance, which otherwise opens autoranged with every label
  // back at its default position.
  initialAxes?: Partial<Layout>;
  initialAnnotationTails?: Record<string, AnnotationTail>;
}

const calcPlotHeight = (plot: HTMLDivElement) => {
  if (window.innerWidth < 900) {
    return 600;
  }

  return window.innerHeight - plot.offsetTop - 22;
};

// HACK: Plotly doesn't allow you to put a violin on top of a scatter plot, so
// we'll improvise.
const movePointsBehindViolinPlot = (plot: HTMLDivElement) => {
  const gl = plot.querySelector(".gl-container") as HTMLElement;
  gl.parentElement!.prepend(gl);

  const svg = plot.querySelector(".main-svg") as HTMLElement;
  svg.style.background = "transparent";

  svg.querySelectorAll(".gridlayer path").forEach((path) => {
    // eslint-disable-next-line no-param-reassign
    (path as HTMLElement).style.stroke = "#000";
    // eslint-disable-next-line no-param-reassign
    (path as HTMLElement).style.strokeOpacity = "0.08";
  });

  // Also nudge the hover label slightly to the right. It can sometimes get in
  // the way when trying to click on a point.
  const hoverlayer = plot.querySelector(".hoverlayer") as HTMLElement;
  hoverlayer.style.transform = "translateX(8px)";
};

// TODO: If we have continuous color data, can we use that to make the y values
// more meaningful (less random)?
const calcY = (
  x: any,
  colorKeys: any,
  colorData: any,
  hiddenLegendValues: any
) => {
  const sRandom = seedrandom("fixedSeed");

  if (!colorData) {
    return x.map(() => sRandom() / 2.1 + 1);
  }

  const y: any = [];
  let offsetY = colorKeys.length - hiddenLegendValues.size;

  colorKeys.forEach((key: string) => {
    if (!hiddenLegendValues.has(key)) {
      colorData.forEach((colorKey: any, i: number) => {
        if (colorKey === key) {
          y[i] = sRandom() / 2.1 + offsetY;
        }
      });

      offsetY -= 1;
    } else {
      colorData.forEach((colorKey: any) => {
        if (colorKey === key) {
          // Make sure values are deterministic.
          sRandom();
        }
      });
    }
  });

  return y as number[];
};

const hasSomeNonNullValue = (array: unknown[]) => {
  for (let i = 0; i < array.length; i += 1) {
    if (array[i] !== null) {
      return true;
    }
  }

  return false;
};

function PrototypeDensity1D({
  data,
  xKey,
  colorMap,
  colorData,
  facetData,
  groupKeys: facetKeysProp,
  continuousColorKey,
  legendDisplayNames,
  facetDisplayNames,
  legendTitle,
  showBuiltinLegend = false,
  colorMatchesFacet = false,
  height,
  hoverTextKey,
  annotationTextKey,
  selectedPoints = null,
  pointVisibility = null,
  useSemiOpaqueViolins = false,
  onClickPoint = () => {},
  onMultiselect = () => {},
  onClickResetSelection = () => {},
  onLoad = () => {},
  hiddenLegendValues = new Set(),
  hiddenFacetValues = new Set(),
  pointSize = 7,
  pointOpacity = 1.0,
  outlineWidth = 0.5,
  palette = DEFAULT_PALETTE,
  annotationFontSize = 12,
  xAxisFontSize = 14,
  yAxisFontSize = 14,
  placeholderEmptyTracks = false,
  enforceSingleFacetSelection = false,
  pointsToAnnotate,
  selectionCount,
  initialAxes = undefined,
  initialAnnotationTails = undefined,
  Plotly,
}: any) {
  const ref = useRef<ExtendedPlotType>(null);
  usePlotResizer(Plotly, ref);

  const axes = useRef<Partial<Layout>>(
    initialAxes ?? {
      xaxis: undefined,
      yaxis: undefined,
    }
  );

  const annotationTails = useRef<Record<string, AnnotationTail>>(
    initialAnnotationTails ? { ...initialAnnotationTails } : {}
  );

  const [dragmode, setDragmode] = useState<Layout["dragmode"]>("zoom");

  useEffect(() => {
    if (onLoad && ref.current) {
      onLoad(ref.current);
    }
  }, [onLoad]);

  useEffect(() => {
    const plot = ref.current;
    return () => {
      (plot as any)?.__facetSelCleanup?.();
      releaseWebglContexts(plot as HTMLElement);
      Plotly.purge(plot as HTMLElement);
    };
  }, [Plotly]);

  const [minX, maxX] = useMemo(() => getRange(data[xKey]), [data, xKey]);

  // When the type of data changes, we force an autoscale by discarding the
  // stored axes.
  const hasEverRunXYInvalidation = useRef(false);

  useEffect(() => {
    // Skipped on mount so this doesn't immediately discard `initialAxes` —
    // see PrototypeScatterPlot's identical guard. Effects always run once,
    // and there is nothing to autoscale away from on a first render anyway:
    // either we were seeded (and want to keep it) or the ref is already
    // empty.
    if (!hasEverRunXYInvalidation.current) {
      hasEverRunXYInvalidation.current = true;
      return;
    }

    axes.current = {
      xaxis: undefined,
      yaxis: undefined,
    };
  }, [data.xLabel, colorData, minX, maxX]);

  const hasEverRunYInvalidation = useRef(false);

  useEffect(() => {
    // Same skip-on-mount reasoning as above, in its own ref since this
    // effect can fire independently of the one above.
    if (!hasEverRunYInvalidation.current) {
      hasEverRunYInvalidation.current = true;
      return;
    }

    axes.current.yaxis = undefined;
    // hiddenFacetValues.size covers the diverged case (color_by/facet_by
    // independent — toggling a facet in the "Facets" panel doesn't touch
    // hiddenLegendValues at all, so without this the axis never re-autoranges
    // and a hidden track leaves a visible gap; worse, re-showing a track can
    // land outside a since-adjusted manual zoom with nothing to indicate it's
    // there). hiddenLegendValues.size still covers the converged case (color
    // legend toggles a facet directly).
  }, [colorMap, colorData, hiddenLegendValues.size, hiddenFacetValues.size]);

  // Update axes when font size changes.
  useEffect(() => {
    const xaxis = axes.current.xaxis;
    const yaxis = axes.current.yaxis;

    if (xaxis) {
      xaxis.tickfont = { size: xAxisFontSize };
      xaxis.title = {
        ...(xaxis.title as object),
        font: { size: xAxisFontSize },
      };
    }

    if (yaxis) {
      yaxis.tickfont = { size: yAxisFontSize };
    }
  }, [xAxisFontSize, yAxisFontSize]);

  useEffect(() => {
    const plot = ref.current as ExtendedPlotType;
    const colorKeys = [...colorMap.keys()];
    // Facet side (drives track assignment). Falls back to color side when
    // not supplied, preserving existing behavior for callers that don't
    // distinguish facet from color.
    const effectiveFacetData = facetData ?? colorData;
    // Annotate explicitly: the component params are typed `any` (see the
    // `}: any)` signature), so facetKeysProp/colorKeys arrive as `any` and the
    // downstream .map/.filter callbacks would otherwise be implicitly-any.
    // Pinning the element type here types the whole violin-trace chain.
    const effectiveFacetKeys: LegendKey[] = facetKeysProp ?? colorKeys;
    // Whether facet_by has real backing (more than just the single
    // LEGEND_ALL "no faceting" bucket) — used below to keep the overlaid
    // scatter points neutral rather than palette.all's own color when
    // facet_by is set but color_by has nothing of its own to show.
    const hasRealFacetBacking = !(
      effectiveFacetKeys.length === 1 && effectiveFacetKeys[0] === LEGEND_ALL
    );
    // hiddenLegendValues is populated purely from clicks on the COLOR
    // legend. It only legitimately describes facet's own tracks when
    // colorMatchesFacet — the two partitions are then either literally the
    // same one (color defers to facet_by) or explicitly read the same
    // source (e.g. facet_by/color_by both "expansion"). Otherwise facet's
    // tracks are a wholly independent partition that merely happens to
    // share LEGEND_BOTH/LEGEND_OTHER's Symbol identity with color's, so
    // hiddenLegendValues must not apply to them — hiddenFacetValues (from
    // the independent "Facets" panel's own click state) drives visibility
    // instead in that case.
    const facetHiddenValues: Set<LegendKey> = colorMatchesFacet
      ? hiddenLegendValues
      : hiddenFacetValues;
    const x = data[xKey] as number[];
    const y = calcY(
      x,
      effectiveFacetKeys,
      effectiveFacetData,
      facetHiddenValues
    );
    const text = hoverTextKey ? data[hoverTextKey] : null;
    const annotationText = annotationTextKey ? data[annotationTextKey] : null;
    const visible = pointVisibility ?? x.map(() => true);

    const contColorData = data[continuousColorKey];

    // Continuous color: order points so the smallest-population bins draw last
    // (on top) and null points on the bottom, matching PrototypeScatterPlot.
    // The reorder lives inside the single continuous trace, so contTraceIndex
    // maps a clicked/selected position back to the original point index. For
    // continuous color, colorData is already the per-point bin-key series.
    const contTraceIndex =
      contColorData && colorData
        ? orderContinuousPointsByBin(
            contColorData,
            colorData,
            colorMap,
            visible
          )
        : [];

    const hasColorOptionsEnabled = colorKeys[0] !== LEGEND_ALL;
    // Only takes effect when templateTrace is actually used as the default
    // (uncolored) trace below — facet_by having its own real categories to
    // color by always wins, this never overrides a real color.
    const inertColor = hasRealFacetBacking ? NEUTRAL_FACET_FILL : palette.all;

    const isSelectionMode =
      dragmode === "select" ||
      dragmode === "lasso" ||
      (selectedPoints && selectedPoints.size > 0);

    // When enforceSingleFacetSelection is on and a select/lasso tool is active,
    // we keep Plotly's real selection running (it does the hit testing and the
    // selected-point rendering) but hide its marquee in favor of our custom,
    // band-clamped one, and constrain the committed result to the anchor facet
    // (see the plotly_selected handler). So the real `dragmode` passes through.
    const useFacetSelection =
      enforceSingleFacetSelection &&
      (dragmode === "select" || dragmode === "lasso");
    const facetSelectionTool = dragmode === "lasso" ? "lasso" : "box";

    const templateTrace = {
      type: "scattergl" as const,
      mode: "markers" as const,
      x: x.map((value: number, i) => (visible[i] ? value : null)),
      y,
      name: "",
      text,
      showlegend: false,
      hoverinfo: "x+text",
      hoverlabel: { bgcolor: inertColor },
      selectedpoints: selectedPoints ? [...selectedPoints] : [],
      marker: {
        color: hexToRgba(inertColor, pointOpacity),
        size: pointSize,
        line: {
          color: inertColor,
          width: outlineWidth,
        },
      },
      selected: { marker: { opacity: 1 } },
      unselected: {
        marker: {
          opacity: isSelectionMode ? 0.5 : 1,
        },
      },
    };

    const makeColorTrace = (
      color: string,
      hasTargetColor: (i: number) => boolean
    ) => {
      return {
        ...templateTrace,
        marker: {
          ...templateTrace.marker,
          line: templateTrace.marker.line
            ? {
                ...templateTrace.marker.line,
                color,
              }
            : undefined,
          color: hexToRgba(color, pointOpacity),
        },
        hoverlabel: { bgcolor: color },
        x: x.map((value: number, i) => {
          return visible[i] && hasTargetColor(i) ? value : null;
        }),
      };
    };

    const defaultTrace = hasColorOptionsEnabled ? null : templateTrace;

    // Paint order for categorical color groups. We stack so the smallest
    // groups end up on top and the catch-all "N/A"/"Other" facet on the
    // bottom, matching the scatter path (getSolidColorGroups). plotlyData is
    // reversed below, so the first key here is drawn last (on top): we sort
    // ascending by visible-point count and pin LEGEND_OTHER/LEGEND_NEITHER
    // (the two catch-all identities — see their shared comment in
    // plotUtils.ts) to the end. Membership and color assignment are
    // unchanged — this only reorders the traces, which matters once color
    // groups span multiple stacks (facet_by !== color_by).
    // When color and facet are the same partition, every track holds exactly
    // one color and the order within a track is moot. When they diverge, a
    // track holds several, and the size that decides burial is the size *in
    // that track* — a color that is rare overall can dominate one track, and a
    // color that is common overall can be the few points someone is looking
    // for in another. Facets partition the points, so the per-facet count is
    // exactly well defined.
    const orderPerFacet = hasRealFacetBacking && !colorMatchesFacet;

    const orderedColorKeysFor = (facet: LegendKey | null) =>
      colorMap && colorData
        ? orderColorKeysByCount(
            colorMap,
            colorData,
            effectiveFacetData,
            facet,
            visible,
            // Density lays points out along x within a track, so x is where
            // concentration is visible.
            [x]
          )
        : ([] as LegendKey[]);

    const makeColorTracesFor = (facet: LegendKey | null) =>
      orderedColorKeysFor(facet)
        .filter((key) => colorMap.get(key))
        .map((key) =>
          makeColorTrace(
            colorMap.get(key),
            (i) =>
              (facet === null || effectiveFacetData[i] === facet) &&
              colorMap.get(key) === colorMap.get(colorData[i])
          )
        );

    const colorTraces = (() => {
      if (!colorMap || !colorData || contColorData) {
        return [];
      }

      // Cross-track order doesn't matter — tracks don't overlap — so the
      // per-facet runs simply concatenate, each internally ordered.
      return orderPerFacet
        ? effectiveFacetKeys.flatMap((facet) => makeColorTracesFor(facet))
        : makeColorTracesFor(null);
    })();

    // TODO: Add support for palette.divergingScale
    const continuousColorTrace = contColorData
      ? {
          ...templateTrace,
          x: contTraceIndex.map((i) => (visible[i] ? x[i] : null)),
          y: contTraceIndex.map((i) => y[i]),
          text: text ? contTraceIndex.map((i) => text[i]) : text,
          // selectedpoints are indices into the (reordered) trace, so map each
          // selected original index to its position in contTraceIndex.
          selectedpoints: contTraceIndex.reduce(
            (acc: number[], origIndex, i) => {
              if (selectedPoints?.has(origIndex)) {
                acc.push(i);
              }
              return acc;
            },
            []
          ),
          hoverlabel: {
            bgcolor: contTraceIndex.map((i) => colorMap.get(colorData[i])),
          },
          marker: {
            size: pointSize,
            color: contTraceIndex.map(
              (i) => contColorData[i] ?? hexToRgba(palette.other, pointOpacity)
            ),
            colorscale: palette.sequentialScale.map(
              ([value, color]: [string, string]) => [
                value,
                hexToRgba(color, pointOpacity),
              ]
            ),
            line: {
              width: outlineWidth,
              color: contTraceIndex.map(
                (i) => contColorData[i] ?? palette.other
              ),
              colorscale: palette.sequentialScale,
            },
          },
        }
      : null;

    // WORKAROUND: We use a special trace to give selected
    // points a dark outline, due to limitations of Plotly.
    const selectionOutlineTrace = {
      ...templateTrace,
      marker: {
        color: "transparent",
        size: pointSize + outlineWidth * 2,
        line: { color: "#000", width: Math.max(outlineWidth, 2) },
      },
      selected: { marker: { opacity: 1 } },
      unselected: { marker: { opacity: 0 } },
      hoverinfo: "skip",
    };

    const templateViolin = {
      type: "violin",
      x,
      y0: 1,
      points: false,
      hoverinfo: "none",
      line: { color: "#666" },
      side: "positive",
      width: 1,
      meanline: { visible: true, color: hexToRgba("#333", 0.5) },
      // The entire legend is usually hidden but we do reveal it when
      // converting the plot to an image. We want to make sure this gets hidden
      // even in that case.
      showlegend: false,
    } as Partial<ViolinData>;

    const violinTraces = effectiveFacetKeys
      .filter((key) => !facetHiddenValues.has(key))
      .map((legendKey, index) => {
        // facet_by is an independent axis from color_by (no fallback). When
        // colorMatchesFacet, colorMap's keys ARE (or coincide with) facet's
        // own keys, so consulting it (with a palette.other fallback for a
        // genuine miss) is correct. When color_by has genuinely diverged,
        // colorMap belongs to a completely different partition —
        // LEGEND_BOTH/LEGEND_OTHER/LEGEND_ALL are shared Symbol identities,
        // so colorMap.get(legendKey) can spuriously SUCCEED with color's
        // own (unrelated) entry for that same shared symbol, not just
        // fail. So the lookup must be skipped entirely when diverged, not
        // merely null-coalesced — every diverged track gets the fixed
        // neutral gray, immune to palette customization.
        let fillcolor = colorMatchesFacet
          ? colorMap.get(legendKey) ?? palette.other
          : NEUTRAL_FACET_FILL;

        if (useSemiOpaqueViolins) {
          fillcolor += "88";
        }

        return {
          ...templateViolin,
          // The "all" bucket is the ungrouped sentinel (a single track holding
          // every point); it carries no meaningful facet label, so render it
          // blank rather than leaking the raw Symbol(All) into the y-axis tick.
          name:
            legendKey === LEGEND_ALL
              ? ""
              : facetDisplayNames?.[legendKey] ??
                legendDisplayNames[legendKey] ??
                String(legendKey),
          x: effectiveFacetData
            ? x.filter(
                (_: any, i: number) => effectiveFacetData[i] === legendKey
              )
            : x,
          y0: effectiveFacetKeys.length - facetHiddenValues.size - index,
          fillcolor,
        };
      })
      // A track whose points are entirely null normally gets dropped (an empty
      // violin is noise). But for expanded plots that drop is what makes a
      // paginated window look short: a transcript the dataset doesn't measure
      // is all-null. When `placeholderEmptyTracks` is set we instead keep it as
      // a labeled "(no data)" slot — its y0 position is already reserved, so it
      // just fills the gap the drop would have left. BANDAID tied to the
      // interim pagination stopgap.
      .map((trace) =>
        placeholderEmptyTracks && isEveryValueNull(trace.x)
          ? {
              ...trace,
              // Prepend, not append: the y-axis ticktext truncates each name to
              // ~25 chars and a transcript label alone already exceeds that, so
              // an appended marker gets cut off. Leading "(no data)" survives.
              name: trace.name ? `❌ (no data) ${trace.name}` : "❌ (no data)",
            }
          : trace
      )
      .filter((trace) => placeholderEmptyTracks || !isEveryValueNull(trace.x));

    // Add an extra violin with a light outline to make
    // it stand out on top many dark-colored points.
    const violinOutlineTraces = effectiveFacetKeys
      .filter((key) => !facetHiddenValues.has(key))
      .map((legendKey, index) => {
        return {
          ...templateViolin,
          line: { color: hexToRgba("#fff", 0.5), width: 4 },
          meanline: { visible: false },
          fillcolor: "transparent",
          x: effectiveFacetData
            ? x.filter(
                (_: any, i: number) => effectiveFacetData[i] === legendKey
              )
            : x,
          y0: effectiveFacetKeys.length - facetHiddenValues.size - index,
        } as any;
      });

    // Dummy traces that exist only to populate Plotly's legend with the right
    // names and colors. Used for downloaded images (where our own legend panel
    // can't be rendered) and, when showBuiltinLegend is set, for the live plot
    // too — the two must agree, hence one definition.
    const legendTraces = colorKeys
      .filter((key) => !hiddenLegendValues.has(key))
      .map((legendKey) => {
        const fillcolor = colorMap.get(legendKey);

        return {
          type: "violin",
          showlegend: true,
          x: [null],
          line: { color: "#666" },
          hoverinfo: "skip",
          name: legendDisplayNames[legendKey],
          fillcolor,
        };
      });

    const plotlyData = [
      ...violinTraces,
      ...violinOutlineTraces,
      defaultTrace,
      ...colorTraces,
      continuousColorTrace,
      selectionOutlineTrace,
    ]
      .filter(Boolean)
      .filter((trace) => hasSomeNonNullValue(trace.x))
      .reverse() as Partial<PlotData>[];

    // Appended after the all-null filter above (which would drop them, their
    // x being [null]) and after the reverse, so the indices that
    // isClickableTrace / isContinuousCurve compare against are untouched.
    if (showBuiltinLegend) {
      legendTraces.forEach((t) =>
        plotlyData.push(t as typeof plotlyData[number])
      );
    }

    const isClickableTrace = (n: number) => {
      return ([
        defaultTrace,
        continuousColorTrace,
        ...colorTraces,
      ] as Partial<PlotData>[]).includes(plotlyData[n]);
    };

    // The continuous trace is the only one whose points are reordered, so its
    // plotly pointIndex must be mapped back through contTraceIndex.
    const isContinuousCurve = (n: number) =>
      continuousColorTrace !== null &&
      (plotlyData[n] as any) === continuousColorTrace;

    const collapseLeftMargin = violinTraces.length === 1;

    // Annotation channel (expansion-selection): label precisely the contacted
    // points, not the re-expanded `selectedPoints`. They coincide off the
    // facet_by === "expansion" collapse, so this is a no-op there; the fallback
    // keeps callers that don't pass the set (e.g. the scatter path) unchanged.
    const pointsForAnnotation = pointsToAnnotate ?? selectedPoints;
    // Count for the "too many to label" fallback. selectionCount is the model
    // count under collapse (selection.size, threaded from the wrapper). TODO:
    // the noun "point(s)" should become the index type's display name (e.g.
    // "model(s)") once that is threaded through; literal for now.
    const annotationCount = selectionCount ?? selectedPoints?.size ?? 0;

    const layout: Partial<Layout> = {
      height: height === "auto" ? calcPlotHeight(plot) : height,
      margin: {
        t: 30,
        r: 15,
        // Floors; automargin grows them to fit the labels and titles.
        b: 50,
        l: collapseLeftMargin ? 15 : 50,
      },
      hovermode: "closest",
      hoverlabel: {
        namelength: -1,
      },

      // Data Explorer has a custom legend, so Plotly's is hidden there. It's
      // toggled on just before capturing a snapshot image (see
      // plot.downloadImage() below), and stays on throughout for embedded
      // plots, which have no custom legend to defer to (showBuiltinLegend).
      showlegend: showBuiltinLegend,
      ...(showBuiltinLegend
        ? { legend: { title: { text: legendTitle } } }
        : {}),

      xaxis: {
        // See PrototypeScatterPlot's note on automargin; same reasoning, and
        // the y axis below has had it all along for its facet-name ticks.
        automargin: true,
        ...(axes.current.xaxis || {
          title: {
            text: data.xLabel,
            font: { size: xAxisFontSize },
            standoff: 8,
          } as any,
          exponentformat: "e",
          type: "linear",
          autorange: true,
          tickfont: { size: xAxisFontSize },
        }),
      },

      yaxis: {
        ...(axes.current.yaxis || { autorange: true }),

        visible:
          Boolean(effectiveFacetData) &&
          effectiveFacetData.length > 0 &&
          violinTraces.length < 40,
        automargin: true,
        tickvals: violinTraces.map((vt) => vt.y0),
        ticktext: violinTraces.map((vt) => truncateFacetLabel(vt.name)),
        tickfont: { size: yAxisFontSize },
        // These ticks are violin-track category labels, not a value scale —
        // a horizontal gridline at each one would suggest a meaningful y
        // value where there isn't one (y is jitter for display only).
        showgrid: false,
      },

      dragmode,

      // enforceSingleFacetSelection: a thin band sweep is mostly-horizontal,
      // which Plotly's default selectdirection "any" auto-reads as an "h"
      // (full-y-axis) selection — spanning every band and bypassing the pointer
      // clamp, since Plotly sets that span programmatically. Force diagonal-only
      // ("d") so every box stays an ordinary drag-cornered rectangle; the
      // interceptor then clamps its height to the anchor band.
      selectdirection: useFacetSelection ? "d" : "any",

      annotations:
        annotationText &&
        pointsForAnnotation &&
        pointsForAnnotation.size <= MAX_POINTS_TO_ANNOTATE
          ? [...pointsForAnnotation]
              .filter(
                // Filter out any annotations associated with missing data. This can
                // happen if the x or y column has changed since the annotations were
                // created.
                (pointIndex) =>
                  typeof x[pointIndex] === "number" &&
                  typeof y[pointIndex] === "number"
              )
              .map((pointIndex) => {
                // Restore any annotation arrowhead position the user may
                // have edited, rescaled for the current annotationFontSize
                // — see resolveAnnotationTail.
                const tail = resolveAnnotationTail(
                  // The "y" here is a literal, not this renderer's own y
                  // (a synthetic jitter value, not a real axis key) — it
                  // exists purely to match captureTransientState's own
                  // hardcoded key format (`${xKey}-${yKey}-${pointIndex}`,
                  // defaulting yKey to "y"), which is what actually seeds
                  // this ref for the export preview. A mismatch here silently
                  // drops every seeded tail, which is exactly what was
                  // happening before this line existed.
                  annotationTails.current[`${xKey}-y-${pointIndex}`],
                  annotationFontSize
                );

                return {
                  x: x[pointIndex],
                  y: y[pointIndex],
                  text: annotationText[pointIndex],
                  visible: visible[pointIndex],
                  xref: "x",
                  yref: "y",
                  arrowhead: 0,
                  arrowwidth: calcAnnotationArrowWidth(annotationFontSize),
                  standoff: 4,
                  font: { size: annotationFontSize },
                  arrowcolor: "#888",
                  bordercolor: "#c7c7c7",
                  bgcolor: "#fff",
                  pointIndex,
                  ax: tail?.ax,
                  ay: tail?.ay,
                };
              })
          : (() => {
              return selectedPoints
                ? [
                    {
                      x: 0.5,
                      y: 0.95,
                      xref: "paper",
                      yref: "paper",
                      text: [
                        annotationCount,
                        annotationCount === 1 ? "point" : "points",
                        "selected",
                      ].join(" "),
                      arrowcolor: "transparent",
                      bordercolor: "#c7c7c7",
                      bgcolor: "#fff",
                    },
                  ]
                : undefined;
            })(),
    };

    const config: Partial<Config> = {
      responsive: true,
      edits: { annotationTail: true },
      displaylogo: false,
      modeBarButtonsToRemove: ["select2d", "lasso2d"],
    };

    Plotly.react(plot, plotlyData, layout, config);

    // enforceSingleFacetSelection: hand the live config to the custom drag
    // layer and (re)install it. The config is read on each mousedown, so it
    // stays current across effect re-runs. Switching tool or toggling the mode
    // off resets the additive (shift-select) state; benign re-renders keep it.
    {
      const prev = (plot as any).__facetSel as FacetSelectionConfig | undefined;
      const resetAdditive =
        !prev ||
        prev.tool !== facetSelectionTool ||
        prev.enabled !== useFacetSelection;

      if (prev && resetAdditive) {
        prev.committedShapes = [];
        prev.selectionRegionKey = null;
      }

      (plot as any).__facetSel = {
        enabled: useFacetSelection,
        tool: facetSelectionTool,
        axis: "y",
        // Facets here are violin tracks at integer y0s. The waterfall uses the
        // same drag layer with a "ranges" model on the x-axis; both rely on
        // facetSelectionDragLayer's shared l2p + axis._offset conversion to map
        // region bounds to exact on-screen pixels (see axisOffset there).
        regionModel: {
          kind: "violinTracks",
          validKeys: new Set<number>(violinTraces.map((vt) => vt.y0 as number)),
        },
        committedShapes: resetAdditive ? [] : prev!.committedShapes,
        selectionRegionKey: resetAdditive ? null : prev!.selectionRegionKey,
      } as FacetSelectionConfig;

      installFacetSelectionDragLayer(plot as any);
    }

    // Keep track of added listeners so we can easily remove them.
    const listeners: [string, (e: any) => void][] = [];

    const on = (eventName: string, callback: (e: any) => void) => {
      plot.on(
        eventName as Parameters<PlotlyHTMLElement["on"]>[0],
        callback as Parameters<PlotlyHTMLElement["on"]>[1]
      );
      listeners.push([eventName, callback]);
    };

    // HACK: The zoom functions provided by Plotly's modebar aren't exposed
    // by its API. The only way to trigger them is by actually clicking the
    // buttons 😕
    const getButton = (attr: string, val: string) =>
      plot.querySelector(
        `.modebar-btn[data-attr="${attr}"][data-val="${val}"]`
      ) as HTMLAnchorElement;

    const zoom = (val: "in" | "out" | "reset") => {
      getButton("zoom", val).click();

      // This redraw fixes a very strange bug where setting the drag mode to
      // select (or lasso) with a filter also applied causes all of the points
      // to disappear.
      Plotly.redraw(plot);
    };

    const assignAnnotationPositions = (pointIndices: number[]) => {
      // Don't both doing the calculation if we're not going to show them.
      if (new Set(pointIndices).size > MAX_POINTS_TO_ANNOTATE) {
        return;
      }

      const fullLayout = (plot as any)._fullLayout;

      calcAnnotationPositions(x, y, pointIndices, fullLayout).forEach(
        ({ pointIndex, ax, ay }) => {
          // See the read site's note: the "y" is a literal matching
          // captureTransientState's own default key format, not this
          // renderer's y.
          annotationTails.current[
            `${xKey}-y-${pointIndex}`
          ] = captureAnnotationTail(ax, ay, annotationFontSize);
        }
      );
    };

    plot.annotateSelected = (points?: number[]) => {
      // Position annotation tails for the given points (the context path passes
      // its representative points; selection + annotation state is already set
      // by setSelectionFromContext). Falls back to the current selection when
      // called with no argument. No longer commits a selection of its own —
      // that "annotate == select" coupling is now split into the two channels.
      const pts = points ?? (selectedPoints ? [...selectedPoints] : []);
      assignAnnotationPositions(pts);
    };

    // After initializing the plot with `autorange` set to true, store what
    // Plotly calculated for the axes zoom level and turn off autorange.
    // The legend is drawn from dummy traces (see legendTraces) that hold no
    // real points, so Plotly's default click behavior toggles the dummy and
    // nothing else: the entry greys out while the plot stays put. Returning
    // false suppresses that, leaving the legend a static key rather than a
    // control that appears to do something. Registered unconditionally — with
    // no legend shown (Data Explorer's case) these never fire.
    // TODO: make these actually filter, as Data Explorer's own legend does.
    on("plotly_legendclick", () => false);
    on("plotly_legenddoubleclick", () => false);

    on("plotly_afterplot", () => {
      if (!axes.current.xaxis || !axes.current.yaxis) {
        axes.current = {
          xaxis: { ...plot.layout.xaxis, autorange: false },
          yaxis: { ...plot.layout.yaxis, autorange: false },
        };
      }

      movePointsBehindViolinPlot(plot);
    });

    on("plotly_relayout", () => {
      // A zoom/resize invalidates the pixel-space committed marquees, so drop
      // the additive (shift-select) state along with the stored axes.
      const cfg = (plot as any).__facetSel as FacetSelectionConfig | undefined;
      if (cfg) {
        cfg.committedShapes = [];
        cfg.selectionRegionKey = null;
      }

      axes.current = {
        xaxis: { ...plot.layout.xaxis, autorange: false },
        yaxis: { ...plot.layout.yaxis, autorange: false },
      };

      plot.layout.annotations?.forEach((annotation) => {
        const { ax, ay } = annotation;

        if (ax != null && ay != null) {
          const { pointIndex } = annotation as { pointIndex: number };
          // Same literal "y" as the other two sites — see the read site's
          // note.
          annotationTails.current[
            `${xKey}-y-${pointIndex}`
          ] = captureAnnotationTail(ax, ay, annotationFontSize);
        }
      });
    });

    on("plotly_click", (e: PlotMouseEvent) => {
      const { curveNumber, pointIndex } = e.points[0];
      const anyModifier =
        e.event.ctrlKey || e.event.metaKey || e.event.shiftKey;

      if (isClickableTrace(curveNumber) && onClickPoint) {
        onClickPoint(
          isContinuousCurve(curveNumber)
            ? contTraceIndex[pointIndex]
            : pointIndex,
          anyModifier
        );
      }

      // WORKAROUND: If you mean to double-click to zoom out and
      // select a point by accident, restore the previous selections.
      const prevAxes = axes.current;
      const prevSelection = selectedPoints;

      setTimeout(() => {
        if (axes.current !== prevAxes && prevSelection) {
          onMultiselect([...prevSelection]);
        }
      }, 100);
    });

    on("plotly_selecting", (e: PlotSelectionEvent) => {
      if (selectedPoints?.size > 0) {
        onClickResetSelection();
      }

      // enforceSingleFacetSelection (density): the band anchor is deferred until
      // the drag box first touches a point (facetSelectionDragLayer leaves
      // selectionRegionKey null on a fresh violin drag). Lock it here to the
      // band of the first contacted point nearest the drag start; from then on
      // the interceptor confines the box to that band.
      //
      // Known limitation (deliberate trade-off): the interceptor clamps the live
      // pointer to the band but does not re-pin Plotly's anchor corner (the
      // mousedown position). So a drag going UPWARD from a start below the
      // resolved violin can momentarily render a box that spans into the
      // neighbour above. Re-pinning the anchor would mean rewriting Plotly's
      // in-progress drag start — invasive, and not worth losing the simple
      // "clamp the pointer" model we settled on, especially for a gesture this
      // uncommon. It is purely a visual artifact: plotly_selected filters the
      // committed points to the anchor band on mouse up, so the selection itself
      // is always unambiguous and its integrity is unaffected.
      if (!useFacetSelection) {
        return;
      }
      const cfg = (plot as any).__facetSel as FacetSelectionConfig | undefined;
      if (
        !cfg ||
        cfg.regionModel.kind !== "violinTracks" ||
        cfg.selectionRegionKey != null || // already resolved (or shift-locked)
        !e?.points?.length // no contact yet — still traversing a gap
      ) {
        return;
      }
      const startY = (plot as any).__facetSelStartCoord as number | undefined;
      let bestBand: number | null = null;
      let bestDist = Infinity;
      e.points.forEach((p) => {
        if (p.data.hoverinfo === "skip" || p.data.hoverinfo === "none") {
          return;
        }
        const origIndex = isContinuousCurve(p.curveNumber)
          ? contTraceIndex[p.pointIndex]
          : p.pointIndex;
        const dist = startY == null ? 0 : Math.abs(y[origIndex] - startY);
        if (dist < bestDist) {
          bestDist = dist;
          bestBand = Math.round(y[origIndex]);
        }
      });
      if (bestBand != null) {
        cfg.selectionRegionKey = bestBand;
      }
    });

    on("plotly_selected", (e: PlotSelectionEvent) => {
      let points =
        e?.points
          .filter(
            (p) => p.data.hoverinfo !== "skip" && p.data.hoverinfo !== "none"
          )
          .map((p) =>
            isContinuousCurve(p.curveNumber)
              ? contTraceIndex[p.pointIndex]
              : p.pointIndex
          ) || [];

      // enforceSingleFacetSelection: Plotly hit-tests across the whole plot, so
      // drop any selected points that aren't in the track the drag was anchored
      // to. A point's track is round(y) (points jitter < 0.5 from their center).
      if (useFacetSelection) {
        const anchorY0 = (plot as any).__facetSel?.selectionRegionKey as
          | number
          | null;
        if (anchorY0 != null) {
          points = points.filter((i) => Math.round(y[i]) === anchorY0);
        }
      }

      assignAnnotationPositions(points);
      onMultiselect(points);
    });

    on("plotly_deselect", () => {
      onClickResetSelection();
    });

    // WORKAROUND: Double-click is supposed to reset the zoom but it only works
    // actually intermittently so we'll do it ourselves.
    on("plotly_doubleclick", () => {
      plot.resetZoom();
    });

    // WORKAROUND: For some reason, autosize only works
    // with width so we'll calculate the height as well.
    on("plotly_autosize", () => {
      if (height === "auto") {
        setTimeout(() => {
          plot.layout.height = calcPlotHeight(plot);
          Plotly.redraw(plot);
        });
      }
    });

    // https://github.com/plotly/plotly.js/blob/55dda47/src/lib/prepare_regl.js
    on("plotly_webglcontextlost", () => {
      // Fixes a bug where points disappear after the browser has been left
      // idle for some time.
      Plotly.redraw(plot);
    });

    // See onWebglContextRestored: complements the handler above for the
    // case where the context is lost to eviction rather than idling —
    // there, nothing is drawable again until *this* fires.
    const stopWatchingContextRestore = onWebglContextRestored(plot, () => {
      Plotly.redraw(plot);
    });

    // Add a few non-standard methods to the plot for convenience.
    plot.setDragmode = (nextDragmode) => {
      const shouldResetSelection =
        plot?.layout?.dragmode !== nextDragmode &&
        (nextDragmode === "select" || nextDragmode === "lasso");

      if (shouldResetSelection && onClickResetSelection) {
        onClickResetSelection();
      }

      setDragmode(nextDragmode);
    };

    plot.zoomIn = () => setTimeout(zoom, 0, "in");
    plot.zoomOut = () => setTimeout(zoom, 0, "out");
    plot.resetZoom = () => setTimeout(zoom, 0, "reset");

    plot.getImageFigure = (options) => {
      // Position comes from the export config, not from a plot style — see
      // legendLayoutFor. Called with no options (the SVG path, or any other
      // caller) it keeps today's right-hand placement.
      const legendPosition = options?.legendPosition ?? "right";
      const edgePadding = options?.edgePadding ?? 0;
      const chromeLineWidth =
        options?.chromeLineWidth ?? DEFAULT_CHROME_LINE_WIDTH;
      // `options.dataLineWidth` is accepted by the shared getImageFigure
      // type but unused here — this renderer draws no identity/regression
      // lines.
      const violinLineWidth =
        options?.violinLineWidth ?? DEFAULT_VIOLIN_LINE_WIDTH;
      const xAxisLabelOverride = options?.xAxisLabel;
      // Same reasoning as SmallMultiplesScatter's xLabel: the override text
      // never reaches the offscreen preview's own props (see
      // ExportImageModal — it's threaded only into this static figure, at
      // export time), so the preview's real automargin never converges on
      // it. Unlike a font-size change (which the preview DOES re-render
      // for, seeding exportMarginFloor with a real, converged size below),
      // there's no real prior render of this exact text to seed from, so a
      // jump from one line to several has to be reserved by hand rather
      // than trusted to a single-shot Plotly.toImage convergence.
      const extraXAxisLinesMargin = calcExtraLinesMargin(
        xAxisLabelOverride ?? data.xLabel,
        xAxisFontSize
      );
      // No yAxisLabel override: the y-axis here is facet/track names via
      // tickvals/ticktext, not a single title the way x is — nothing for
      // that option to mean on this renderer.
      const legendTitleOverride = options?.legendTitle;
      const legendItemLabelsOverride = options?.legendItemLabels;

      // Rebuilt rather than reusing the outer `legendTraces` — that one is
      // fixed at whatever legendTitle/legendDisplayNames were at the last
      // regular render, with no way to thread an export-only override
      // through it. Same filter/order as that one, so which items show up
      // is unaffected — only the names (by index) can differ.
      const exportLegendTraces = colorKeys
        .filter((key: LegendKey) => !hiddenLegendValues.has(key))
        .map((legendKey: LegendKey, i: number) => ({
          type: "violin",
          showlegend: true,
          x: [null],
          line: { color: "#666" },
          hoverinfo: "skip",
          name: legendItemLabelsOverride?.[i] ?? legendDisplayNames[legendKey],
          fillcolor: colorMap.get(legendKey),
        }));

      // Split plot.data into a violin layer and a points layer, overriding the
      // two violin traces' curve widths in place as they're sorted —
      // identified by reference against violinTraces/violinOutlineTraces, not
      // by position: plotlyData (and so plot.data, which Plotly builds from it
      // without cloning) is a `.filter().reverse()` of the concatenation those
      // two arrays start, so an index-based split would grab the wrong traces
      // entirely once any track is missing or all-null. At the default width
      // the violin override is a no-op copy: DEFAULT_VIOLIN_LINE_WIDTH is
      // `undefined` on templateViolin (Plotly's own default of 2) and
      // calcViolinOutlineWidth(2) is 4, matching violinOutlineTraces' existing
      // hardcoded literal exactly.
      //
      // The split itself exists because of the same limitation
      // movePointsBehindViolinPlot (top of file) works around on screen:
      // Plotly can't stack a violin (SVG) trace under a scattergl (WebGL
      // canvas) one via trace order — the two always paint in a fixed relative
      // order regardless of where they sit in `data`. That DOM hack only runs
      // against this component's own mounted graph div; `Plotly.toImage`
      // renders into its own disconnected, temporary one that our
      // `plotly_afterplot` handler never sees, so a static export got the
      // wrong order no matter what `data` said. Rendering each group as its
      // own image and compositing them (see overlayFigure, and
      // ExportImageModal's renderPaddedImage) reproduces the on-screen
      // stacking without relying on a DOM that doesn't exist at export time.
      const violinLayerData: object[] = [];
      const pointsLayerData: object[] = [];

      plot.data.forEach((trace) => {
        if (violinTraces.includes(trace as any)) {
          violinLayerData.push({
            ...trace,
            line: { ...(trace as any).line, width: violinLineWidth },
          });
          return;
        }

        if (violinOutlineTraces.includes(trace as any)) {
          violinLayerData.push({
            ...trace,
            line: {
              ...(trace as any).line,
              width: calcViolinOutlineWidth(violinLineWidth),
            },
          });
          return;
        }

        pointsLayerData.push(trace);
      });

      // The "inner" half of edgePadding — see PrototypeScatterPlot's identical
      // treatment. `automargin` stays on for this axis (used by the points
      // layer below); it just measures a bigger need now that standoff asks
      // for more.
      const sharedXAxis = {
        ...plot.layout.xaxis,
        ...calcChromeAxisOverrides(chromeLineWidth),
        title: {
          ...(plot.layout.xaxis as any)?.title,
          ...(xAxisLabelOverride !== undefined
            ? { text: xAxisLabelOverride }
            : {}),
          standoff:
            ((plot.layout.xaxis as any)?.title?.standoff ?? 8) + edgePadding,
        },
      };

      const sharedYAxis = {
        ...plot.layout.yaxis,
        ...calcChromeAxisOverrides(chromeLineWidth),
      };

      // See exportMarginFloor: seeds the single-shot export render with the
      // margin this instance already converged to, rather than the small
      // requested floor that `plot.layout.margin` always holds. `b` matches
      // the standoff growth above (see PrototypeScatterPlot) plus
      // extraXAxisLinesMargin — deliberately NOT also added to standoff above:
      // standoff is the fixed gap before the title anchor, which a native
      // multi-line title already grows away from downward on its own (one
      // lineHeight per extra line, the same quantity calcExtraLinesMargin
      // computes) — adding it to standoff too pushed the anchor down by that
      // same amount a second time, leaving the newly reserved room stranded
      // above an unmoved (bottom-flush) label instead of under it. `l` isn't
      // grown to match — there's no y-axis label standoff to seed room for
      // here.
      //
      // Computed once and reused as a fixed value for *both* layers below (not
      // just a floor for the violin layer's own axes, which have automargin
      // turned off) — see overlayFigure's own comment on why that matters for
      // the composite to land pixel-for-pixel.
      const sharedMargin = exportMarginFloor(plot, {
        b: edgePadding + extraXAxisLinesMargin,
      });

      const sharedLayout: Partial<Layout> = {
        ...plot.layout,
        xaxis: sharedXAxis,
        yaxis: sharedYAxis,
        margin: sharedMargin,
        // See PrototypeScatterPlot's note: the font follows the axis font
        // size so the legend scales with everything else in the figure.
        ...legendLayoutFor(legendPosition, {
          title: legendTitleOverride ?? legendTitle,
          fontSize: xAxisFontSize,
        }),
      };

      // Under showBuiltinLegend the legend traces are already in plot.data;
      // appending them again would double every legend entry. A hidden legend
      // appends nothing — and when they're already in plot.data, `showlegend:
      // false` from legendLayoutFor is what hides them.
      //
      // Appended to *both* layers below, not just the one the legend visually
      // belongs with: a "right"/"above" legend makes Plotly reserve margin for
      // it via automargin, same as it does for axis ticks and titles — see
      // sharedMargin's own comment on why the two layers' margins have to
      // match, which now depends on the legend reservation matching too.
      const legendTracesToAppend =
        showBuiltinLegend || !wantsLegendTraces(legendPosition)
          ? []
          : exportLegendTraces;

      const pointsLayerFigure = cloneFigureForExport(
        [...pointsLayerData, ...legendTracesToAppend],
        {
          ...sharedLayout,
          // Per-point callouts (and the "(N points selected)" summary)
          // carry their own opaque bgcolor specifically so they stay
          // legible over whatever's beneath them — that has to mean the
          // violin layer too, so they move there below rather than
          // drawing here, underneath it.
          annotations: undefined,
        }
      );

      // Rendered as a second image and composited on top of the points layer
      // (see ExportImageModal's renderPaddedImage) — never merged into one
      // figure, for the reason in the comment above the split. Axis
      // *decoration* (ticks, title, gridlines, the plot's own background rect)
      // is stripped so only the violin shapes themselves draw; the points
      // layer already drew all of that once and a second, transparent copy
      // would just retrace the same lines. `automargin` is explicitly off on
      // both axes for the same reason — this layer has no tick/title text of
      // its own to measure, so there's nothing for it to converge to *but* the
      // shared floor, and leaving it on would risk Plotly settling on a
      // different, silently misaligned size than the points layer, which still
      // has real content of its own to measure. The legend, unlike axis
      // decoration, is kept (inherited from sharedLayout, unmodified) — see
      // legendTracesToAppend's comment for why dropping it here was the actual
      // bug.
      const violinLayerFigure = cloneFigureForExport(
        [...violinLayerData, ...legendTracesToAppend],
        {
          ...sharedLayout,
          paper_bgcolor: "transparent",
          plot_bgcolor: "transparent",
          xaxis: {
            ...sharedXAxis,
            automargin: false,
            title: undefined,
            showticklabels: false,
            ticks: "" as const,
            showgrid: false,
            zeroline: false,
          },
          yaxis: {
            ...sharedYAxis,
            automargin: false,
            showticklabels: false,
            ticks: "" as const,
            showgrid: false,
            zeroline: false,
          },
        }
      );

      // A label dragged in ExportImageModal's own live "Adjust label
      // positions" preview — applied here, not on pointsLayerFigure: per-point
      // callouts live only on the violin/overlay layer (see annotations:
      // undefined above), so this is the one place they actually are.
      violinLayerFigure.layout.annotations = applyAnnotationTailOverrides(
        violinLayerFigure.layout.annotations as any[] | undefined,
        options?.annotationTailOverrides,
        annotationFontSize
      );

      return { ...pointsLayerFigure, overlayFigure: violinLayerFigure };
    };

    // Currently unreachable from the UI — PrototypePlotsControls routes every
    // download through ExportImageModal instead, which composites
    // getImageFigure's overlayFigure via renderPaddedImage. This does not:
    // Plotly.downloadImage only knows `figure.data`/`figure.layout`, so it
    // would silently drop the violin layer and save points alone. Fine as dead
    // code; would need the same compositing if ever revived.
    plot.downloadImage = (options) => {
      const figure = plot.getImageFigure();

      if (figure) {
        Plotly.downloadImage(figure, options);
      }
    };

    plot.isPointInView = (pointIndex: number) => {
      const px = x[pointIndex] as number;
      const py = y[pointIndex] as number;
      const xrange = plot.layout.xaxis.range as [number, number];
      const yrange = plot.layout.yaxis.range as [number, number];

      return (
        px >= xrange[0] && px <= xrange[1] && py >= yrange[0] && py <= yrange[1]
      );
    };

    plot.xValueMissing = (pointIndex: number) => {
      return typeof data[xKey][pointIndex] !== "number";
    };

    // Not possible
    plot.yValueMissing = () => false;

    return () => {
      listeners.forEach(([eventName, callback]) =>
        plot.removeListener?.(eventName, callback)
      );
      stopWatchingContextRestore();
    };
  }, [
    data,
    xKey,
    colorMap,
    colorData,
    facetData,
    facetKeysProp,
    continuousColorKey,
    legendDisplayNames,
    facetDisplayNames,
    legendTitle,
    showBuiltinLegend,
    colorMatchesFacet,
    hoverTextKey,
    annotationTextKey,
    height,
    selectedPoints,
    pointsToAnnotate,
    selectionCount,
    onClickPoint,
    onMultiselect,
    onClickResetSelection,
    pointVisibility,
    useSemiOpaqueViolins,
    dragmode,
    hiddenLegendValues,
    hiddenFacetValues,
    pointSize,
    pointOpacity,
    outlineWidth,
    palette,
    annotationFontSize,
    xAxisFontSize,
    yAxisFontSize,
    placeholderEmptyTracks,
    enforceSingleFacetSelection,
    Plotly,
  ]);

  return <div ref={ref} />;
}

export default function LazyPrototypeDensity1D({ data, ...otherProps }: Props) {
  const PlotlyLoader = usePlotlyLoader();

  return (
    <PlotlyLoader version="module">
      {(Plotly) =>
        data ? (
          <PrototypeDensity1D data={data} Plotly={Plotly} {...otherProps} />
        ) : null
      }
    </PlotlyLoader>
  );
}
